import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleCopyUrl,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  Input,
  RadioButton,
  TextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function ALTFTCParentCoaching(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const yesNoOptions = [
    {
      _id: "1",
      name: "Yes",
    },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="FosterHomeName"
                    label="Foster Home Name:"
                    error={errors.FosterHomeName}
                    value={data.FosterHomeName["@value"]}
                    onChange={handleChange}
                    identical={compare("FosterHomeName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="DateOfSession"
                    label="Date:"
                    type="date"
                    error={errors.DateOfSession}
                    value={data.DateOfSession["@value"]}
                    identical={compare("DateOfSession")}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <u>Section A: Positive Behaviors</u>
              <br />
              <b>
                What positive did we discuss during our last coaching session?
              </b>
              <Input
                name="PositiveLastSession"
                error={errors.PositiveLastSession}
                value={data.PositiveLastSession["@value"]}
                identical={compare("PositiveLastSession")}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What interventions/strategies were used to reinforce that
                positive behavior(s)?
              </b>
              <Input
                name="PositiveStrategies"
                error={errors.PositiveStrategies}
                value={data.PositiveStrategies["@value"]}
                identical={compare("PositiveStrategies")}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                How did you reinforce those positive behaviors so they would
                continue?
              </b>
              <Input
                name="HowReinforce"
                error={errors.HowReinforce}
                value={data.HowReinforce["@value"]}
                identical={compare("HowReinforce")}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>Are those positive behaviors still happening?</b>
              <RadioButton
                name="PositivityContinues"
                options={yesNoOptions}
                error={errors.PositivityContinues}
                value={data.PositivityContinues["@value"]}
                onChange={handleChange}
                identical={compare("PositivityContinues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.PositivityContinues["@value"] === "0" ? (
                <Input
                  name="DoDifferent"
                  label="If not, what can we do differently so they will happen again?"
                  error={errors.DoDifferent}
                  value={data.DoDifferent["@value"]}
                  onChange={handleChange}
                  identical={compare("DoDifferent")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              <b>What has gone well this week?</b>
              <Input
                name="GoneWell"
                error={errors.GoneWell}
                value={data.GoneWell["@value"]}
                onChange={handleChange}
                identical={compare("GoneWell")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What new or emerging positive behaviors can we focus on now?
              </b>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="FollowsDirections"
                    label="Follows directions"
                    checked={data.FollowsDirections["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="FollowsRules"
                    label="Follows house rules"
                    checked={data.FollowsRules["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="CompletesHomework"
                    label="Completes homework"
                    checked={data.CompletesHomework["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="TellsTruth"
                    label="Tells the truth"
                    checked={data.TellsTruth["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="PerformsWell"
                    label="Performs well in school"
                    checked={data.PerformsWell["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="ExpressesFeelings"
                    label="Expresses feelings/thoughts appropriately"
                    checked={data.ExpressesFeelings["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="RespectsBoundaries"
                    label="Respects boundaries"
                    checked={data.RespectsBoundaries["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="AppropriateTone"
                    label="Uses appropriate language/tone"
                    checked={data.AppropriateTone["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="CompletesChores"
                    label="Completes chores"
                    checked={data.CompletesChores["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="RespectsProperty"
                    label="Respects others' property"
                    checked={data.RespectsProperty["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="GoodHygiene"
                    label="Shows good hygiene"
                    checked={data.GoodHygiene["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="OtherPositive"
                    label="Other specific behavior(s) (keep it behavioral: what it looks &amp; sounds like)"
                    checked={data.OtherPositive["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  {data.OtherPositive["@value"] === "true" && (
                    <Input
                      name="OtherPositiveDetails"
                      error={errors.OtherPositiveDetails}
                      value={data.OtherPositiveDetails["@value"]}
                      onChange={handleChange}
                      identical={compare("OtherPositiveDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <b>Where, when, and how often do these behaviors happen?</b>
              <Input
                name="PositiveCircumstances"
                error={errors.PositiveCircumstances}
                value={data.PositiveCircumstances["@value"]}
                onChange={handleChange}
                identical={compare("PositiveCircumstances")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What reinforcement and strategies can we use to support this new
                positive behavior?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="VerbalPraise"
                      label="Verbal praise"
                      checked={data.VerbalPraise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NaturalConsequence"
                      label="Natural onsequence"
                      checked={data.NaturalConsequence["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="NonVerbalPraise"
                      label="Non-verbal praise (high 5, thumbs up, hug)"
                      checked={data.NonVerbalPraise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Reward"
                      label="Reward/incentive/extra privilege"
                      checked={data.Reward["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ReminderOfReward"
                      label="Reminder of possible reward"
                      checked={data.ReminderOfReward["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TrackPositiveBehavior"
                      label="Track positive behavior"
                      checked={data.TrackPositiveBehavior["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="BehaviorContract"
                      label="Behavior contract"
                      checked={data.BehaviorContract["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherStrategy"
                      label="Other:"
                      checked={data.OtherStrategy["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {data.OtherStrategy["@value"] === "true" && (
                  <div className="row">
                    <div className="col"></div>
                    <div className="col">
                      <Input
                        name="OtherStrategyDetails"
                        error={errors.OtherStrategyDetails}
                        value={data.OtherStrategyDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherStrategyDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                )}
              </div>
              <u>Section B: Core Elements Check-In</u>
              <RadioButton
                name="DailyCheckIns"
                label="Did you consistently do “Daily Check-Ins” since we last talked?"
                options={yesNoOptions}
                error={errors.DailyCheckIns}
                value={data.DailyCheckIns["@value"]}
                onChange={handleChange}
                identical={compare("DailyCheckIns")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="CheckInPositiveInfo"
                label="What specific things were you able to praise the youth for? What concerns or goals were shared?"
                error={errors.CheckInPositiveInfo}
                value={data.CheckInPositiveInfo["@value"]}
                onChange={handleChange}
                identical={compare("CheckInPositiveInfo")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                Did your family have at least one "Family Meeting" since we last
                talked?
              </b>
              <RadioButton
                name="FamilyMeeting"
                options={yesNoOptions}
                error={errors.FamilyMeeting}
                value={data.FamilyMeeting["@value"]}
                onChange={handleChange}
                identical={compare("FamilyMeeting")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FamilyMeetingReviewed"
                label="What positives and concerns about your family life were shared?"
                error={errors.FamilyMeetingReviewed}
                value={data.FamilyMeetingReviewed["@value"]}
                onChange={handleChange}
                identical={compare("FamilyMeetingReviewed")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                Did you and your child / youth escape any potential "Power
                Struggles" since we last talked?
              </b>
              <RadioButton
                name="PowerStruggle"
                options={yesNoOptions}
                error={errors.PowerStruggle}
                value={data.PowerStruggle["@value"]}
                onChange={handleChange}
                identical={compare("PowerStruggle")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What techniques did you use to interrupt the "Conflict Cycle?"
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PreTeaching"
                      label="Used pre-teaching"
                      checked={data.PreTeaching["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="IMessage"
                      label="Used an “I” message"
                      checked={data.IMessage["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="NoArguing"
                      label="Did not argue"
                      checked={data.NoArguing["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RegainComposure"
                      label="Took time to regain composure"
                      checked={data.RegainComposure["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Separated"
                      label="Separated misbehavior from attack"
                      checked={data.Separated["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Concise"
                      label="Stayed short/to the point"
                      checked={data.Concise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Ignored"
                      label="Ignored"
                      checked={data.Ignored["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="WalkedAway"
                      label="Walked away"
                      checked={data.WalkedAway["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Neutral"
                      label="Said something neutral"
                      checked={data.Neutral["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NoButtons"
                      label="Did not let “button(s)” get pushed"
                      checked={data.NoButtons["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PowerStruggleNA"
                      label="N/A"
                      checked={data.PowerStruggleNA["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherPowerStruggleStrategy"
                      label="Other"
                      checked={
                        data.OtherPowerStruggleStrategy["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col"></div>
                  <div className="col">
                    {data.OtherPowerStruggleStrategy["@value"] === "true" && (
                      <Input
                        name="OtherPowerStruggleStrategyDetails"
                        error={errors.OtherPowerStruggleStrategyDetails}
                        value={data.OtherPowerStruggleStrategyDetails["@value"]}
                        defaultValue={
                          data.OtherPowerStruggleStrategyDetails["@value"]
                        }
                        identical={compare("OtherPowerStruggleStrategyDetails")}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <b>
                If you were not successful in avoiding "Power Struggles", what
                did you do to recover/restore the relationship?
              </b>
              <Input
                name="RestoreRelationship"
                error={errors.RestoreRelationship}
                value={data.RestoreRelationship["@value"]}
                onChange={handleChange}
                identical={compare("RestoreRelationship")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What "Family Fun" activities did your family do since we last
                talked?
              </b>
              <Input
                name="FamilyFunTime"
                error={errors.FamilyFunTime}
                value={data.FamilyFunTime["@value"]}
                onChange={handleChange}
                identical={compare("FamilyFunTime")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                Has your family had the opportunity to do any of the following
                since our last session:
              </b>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="RevisitRules"
                    label="Revisit house rules"
                    checked={data.RevisitRules["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="AddressIdentity"
                    label="Address issues of cultural or individual identity"
                    checked={data.AddressIdentity["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="ImplementBehaviorContract"
                    label="Implement a behavior contract"
                    checked={
                      data.ImplementBehaviorContract["@value"] === "true"
                    }
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="LifeSkill"
                    label="Work on a specific life skill"
                    checked={data.LifeSkill["@value"] === "true"}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <u>Section C: Problematic Behaviors (Identification)</u>
              <b>
                What problematic behaviors did we identify during our last
                session?
              </b>
              <Input
                name="PreviousProblematicBehaviors"
                error={errors.PreviousProblematicBehaviors}
                value={data.PreviousProblematicBehaviors["@value"]}
                onChange={handleChange}
                identical={compare("PreviousProblematicBehaviors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What interventions/strategies were used to decrease those
                problematic behaviors?
              </b>
              <Input
                name="PreviousStrategies"
                error={errors.PreviousStrategies}
                value={data.PreviousStrategies["@value"]}
                onChange={handleChange}
                identical={compare("PreviousStrategies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>Are these problematic behaviors still happening?</b>
              <RadioButton
                name="ProblemsContinue"
                options={yesNoOptions}
                error={errors.ProblemsContinue}
                value={data.ProblemsContinue["@value"]}
                onChange={handleChange}
                identical={compare("ProblemsContinue")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="NewInterventions"
                label="Do we need to continue our interventions, or is it time to try some new interventions? If new interventions, let's plan for those:"
                error={errors.NewInterventions}
                value={data.NewInterventions["@value"]}
                onChange={handleChange}
                identical={compare("NewInterventions")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                Are there any new problematic behaviors we need to focus on now?
              </b>
              <Input
                name="FocusProblems"
                error={errors.FocusProblems}
                value={data.FocusProblems["@value"]}
                onChange={handleChange}
                identical={compare("FocusProblems")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What are the details of these new problematic behavior(s)?
                (when/where/how often):
              </b>
              <Input
                name="NewProblemsDetails"
                error={errors.NewProblemsDetails}
                value={data.NewProblemsDetails["@value"]}
                onChange={handleChange}
                identical={compare("NewProblemsDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What interventions and strategies will we use to decrease these
                problematic behaviors?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PrivilegeRemoval"
                      label="Privilege removal"
                      checked={data.PrivilegeRemoval["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ProblemSolving"
                      label="Problem-solving steps"
                      checked={data.ProblemSolving["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Restitution"
                      label="Restitution"
                      checked={data.Restitution["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="BehaviorContractReduce"
                      label="Behavior contract"
                      checked={data.BehaviorContractReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="TimeInOut"
                      label="Time out/time in"
                      checked={data.TimeInOut["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TrackBehavior"
                      label="Track behavior (describe)"
                      checked={data.TrackBehavior["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="WorkChore"
                      label="Work chore"
                      checked={data.WorkChore["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RewardReduce"
                      label="Reward/incentive for alternative/opposite behavior"
                      checked={data.RewardReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="NaturalConsequenceReduce"
                      label="Natural consequence (what naturally happened without your intervention)"
                      checked={
                        data.NaturalConsequenceReduce["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="VerbalPraiseReduce"
                      label="Verbal praise for alternative/opposite behavior"
                      checked={data.VerbalPraiseReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="LogicalConsequence"
                      label="Logical consequence"
                      checked={data.LogicalConsequence["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="DirectionWithWarning"
                      label="Effective direction with one warning"
                      checked={data.DirectionWithWarning["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="OtherReduce"
                      label="Other (keep it behavioral - what it looks/sounds like)"
                      checked={data.OtherReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherReduce["@value"] === "true" && (
                      <Input
                        name="OtherReduceDetails"
                        error={errors.OtherReduceDetails}
                        value={data.OtherReduceDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherReduceDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <b>
                Do we see any of the problematic behaviors as being related to
                trauma the child has experienced?:
              </b>
              <RadioButton
                name="TraumaRelated"
                options={yesNoOptions}
                error={errors.TraumaRelated}
                value={data.TraumaRelated["@value"]}
                onChange={handleChange}
                identical={compare("TraumaRelated")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                How do the contents of the child's "Invisible Suitcase"
                influence this behavior?
              </b>
              <Input
                name="InfluenceThoughts"
                error={errors.InfluenceThoughts}
                value={data.InfluenceThoughts["@value"]}
                onChange={handleChange}
                identical={compare("InfluenceThoughts")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What trauma-informed strategies should we try? Are there things
                we should avoid?
              </b>
              <Input
                name="StrategiesToTryOrAvoid"
                error={errors.StrategiesToTryOrAvoid}
                value={data.StrategiesToTryOrAvoid["@value"]}
                onChange={handleChange}
                identical={compare("StrategiesToTryOrAvoid")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                If we don't see this behavior as trauma driven, do we see the
                behavior as an expression of an unmet need?
              </b>
              <RadioButton
                name="NeedRelated"
                options={yesNoOptions}
                error={errors.NeedRelated}
                value={data.NeedRelated["@value"]}
                onChange={handleChange}
                identical={compare("NeedRelated")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.NeedRelated["@value"] === "1" ? (
                <>
                  <b>
                    What more socially appropriate ways are there the child
                    could be taught to meet this need?
                  </b>
                  <Input
                    name="LessonToMeetNeed"
                    error={errors.LessonToMeetNeed}
                    value={data.LessonToMeetNeed["@value"]}
                    onChange={handleChange}
                    identical={compare("LessonToMeetNeed")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              <u>Section D: Self Care</u>
              <b>
                On a scale of 1 (not stressed) to 10 (extremely stressed), you
                reported a stress level at our last coaching session of:
              </b>
              <Input
                name="FeelingLastMeeting"
                error={errors.FeelingLastMeeting}
                value={data.FeelingLastMeeting["@value"]}
                onChange={handleChange}
                identical={compare("FeelingLastMeeting")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>What is your stress level today?</b>
              <Input
                name="FeelingToday"
                error={errors.FeelingToday}
                value={data.FeelingToday["@value"]}
                onChange={handleChange}
                identical={compare("FeelingToday")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>If there was a change, why do you think that is?</b>
              <Input
                name="ReasonForChange"
                error={errors.ReasonForChange}
                value={data.ReasonForChange["@value"]}
                onChange={handleChange}
                identical={compare("ReasonForChange")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What self-care activities do you find helpful? Which will you
                focus on between now and next time we meet? Are there any
                barriers you are facing to self-care?
              </b>
              <Input
                name="SelfCareStrategies"
                error={errors.SelfCareStrategies}
                value={data.SelfCareStrategies["@value"]}
                onChange={handleChange}
                identical={compare("SelfCareStrategies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>What specifically can SAFY do to help you with self-care?</b>
              <Input
                name="SAFYHelp"
                error={errors.SAFYHelp}
                value={data.SAFYHelp["@value"]}
                onChange={handleChange}
                identical={compare("SAFYHelp")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>Other issues discussed during this meeting:</b>
              <TextArea
                name="AdditionalComments"
                rows="5"
                error={errors.AdditionalComments}
                value={data.AdditionalComments["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>Date of Next Meeting:</b>
              <Input
                name="DateOfNextMeeting"
                type="date"
                error={errors.DateOfNextMeeting}
                value={data.DateOfNextMeeting["@value"]}
                identical={compare("DateOfNextMeeting")}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default ALTFTCParentCoaching;
